import type { Slug } from "schemas/generated/utils";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { AddAnalyticsCustomDomainModal } from "@v2/features/settings/shop/modals/AddAnalyticsCustomDomainModal";
import { useSubscriptionInfo } from "@v2/hooks/subscription";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { Check } from "lucide-react";
import { useForm } from "react-hook-form";
import { truncateDomain } from "replo-utils/lib/url";
import { slugSchema } from "schemas/utils";
import { z } from "zod";

const EmptyCustomDomainsState: React.FC<{
  openAddCustomDomainModal: () => void;
}> = ({ openAddCustomDomainModal }) => (
  <div className="flex flex-row justify-end gap-2">
    <Button variant="primary" size="base" onClick={openAddCustomDomainModal}>
      Add Custom Domain
    </Button>
  </div>
);

const CustomDomainsList: React.FC<{
  domains: string[];
  openAddCustomDomainModal: () => void;
}> = ({ domains, openAddCustomDomainModal }) => (
  <div className="flex w-full flex-col gap-4">
    <div className="flex flex-row justify-end">
      <Button variant="link" size="sm" onClick={openAddCustomDomainModal}>
        Add Custom Domain
      </Button>
    </div>
    <div className="flex flex-col gap-2">
      {domains.map((domain, index) => (
        <div
          key={index}
          className="border-0.5 border-border flex items-center justify-between rounded-sm p-3"
        >
          <span className="text-muted text-sm" title={domain}>
            {truncateDomain(domain)}
          </span>
          <Check size={16} className="text-muted" />
        </div>
      ))}
    </div>
  </div>
);

const GroupNamesList: React.FC<{
  groupNames: string[];
  workspaceId?: string;
  projectId?: string;
}> = ({ groupNames, workspaceId, projectId }) => {
  const {
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<{ slug: Slug }>({
    resolver: zodResolver(z.object({ slug: slugSchema })),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { mutateAsync: addShortName, isPending: isAddingShortName } =
    workspaceRouter.createShortName.useMutation({
      onSuccess: (response) => {
        toast.success({
          title: "Group Name Added",
          description: `${response.value} has been added successfully.`,
        });
        void workspaceUtils.getShortNames.invalidate({
          workspaceId,
          projectId,
        });
      },
      onError: () => {
        setIsAdding(false);
        reset();
      },
    });

  const [isAdding, setIsAdding] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const logEvent = useLogAnalytics();
  const { subscriptionInfo } = useSubscriptionInfo();
  const subscriptionTier = subscriptionInfo?.tier || "free";
  const newGroupName = watch("slug");

  const handleSave = handleSubmit(async ({ slug }) => {
    await addShortName({
      workspaceId,
      projectId,
      slug,
    });
    logEvent("experiment.groupName.add", {
      billingPlanTier: subscriptionTier,
    });
    reset();
    setIsAdding(false);
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && isAdding) {
      setIsAdding(false);
      reset();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("slug", e.target.value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {!isAdding && groupNames.length !== 0 && (
        <div className="flex flex-row justify-end">
          <Button variant="link" size="sm" onClick={() => setIsAdding(true)}>
            Add Group Name
          </Button>
        </div>
      )}
      {groupNames.length === 0 && !isAdding && (
        <div className="flex flex-row justify-end">
          <Button
            variant="primary"
            size="base"
            onClick={() => setIsAdding(true)}
          >
            Add Group Name
          </Button>
        </div>
      )}
      {isAdding && (
        <div className="flex flex-col gap-1">
          <Input
            ref={inputRef}
            size="base"
            type="text"
            placeholder="Enter new group name"
            maxLength={256}
            onChange={handleInputChange}
            value={newGroupName}
            onKeyDown={handleKeyDown}
            invalid={Boolean(errors.slug)}
          />
          {!isAddingShortName && (
            <span className="text-rose text-xs font-normal">
              {errors?.slug?.message}
            </span>
          )}
          <div className="flex flex-row justify-end">
            <Button
              variant="primary"
              size="base"
              onClick={() => void handleSave()}
              disabled={!isValid}
            >
              Save Group Name
            </Button>
          </div>
        </div>
      )}
      {groupNames.length > 0 && (
        <div className="flex flex-col gap-2">
          {groupNames.map((name) => (
            <div
              key={name}
              className="border-0.5 border-border flex items-center justify-between rounded-sm p-3"
            >
              <span className="text-muted text-sm">{name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CustomDomainsSection: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();
  const [customDomainModalOpen, setCustomDomainModalOpen] =
    React.useState(false);

  const { data: customDomains } = workspaceRouter.getV1CustomDomains.useQuery({
    workspaceId: workspaceId ?? undefined,
    projectId,
  });

  const openAddCustomDomainModal = () => {
    setCustomDomainModalOpen(true);
  };

  return (
    <>
      {customDomains && customDomains.length > 0 ? (
        <CustomDomainsList
          domains={customDomains.map((domain) => domain.value)}
          openAddCustomDomainModal={openAddCustomDomainModal}
        />
      ) : (
        <EmptyCustomDomainsState
          openAddCustomDomainModal={openAddCustomDomainModal}
        />
      )}
      <AddAnalyticsCustomDomainModal
        open={customDomainModalOpen}
        onOpenChange={setCustomDomainModalOpen}
        workspaceId={workspaceId ?? null}
        projectId={projectId ?? null}
      />
    </>
  );
};

export const GroupNamesSection: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();

  const { data: shortNames } = workspaceRouter.getShortNames.useQuery({
    workspaceId: workspaceId ?? undefined,
    projectId,
  });

  return (
    <GroupNamesList
      groupNames={shortNames?.map((shortName) => shortName.value) ?? []}
      workspaceId={workspaceId ?? undefined}
      projectId={projectId}
    />
  );
};
